import React from 'react';
import { Col, Collapse, Row } from 'antd';
import { AddressResponse, HospiceOrganizationWithDetailsResponse } from '../../models/OrganizationModels';
import RoundedButton from '../RoundedButton';
import { useNavigate } from 'react-router-dom';
import '../../styles/_orgs.scss';
import PlusButton from '../PlusButton';
import OrganizationHeader from './OrganizationHeader';
import RoundedButtonWithBorder from '../RoundedButtonWithBorder';
import moment from 'moment';
import { Utility } from '../../utilities/Utility';

const { Panel } = Collapse;

type Props = {
    data: HospiceOrganizationWithDetailsResponse[];
    openModal: Function;
    setSelectedLocation: React.Dispatch<React.SetStateAction<string>>;
    setOpenLocationModal: Function;
    setSelectedOrganization: React.Dispatch<React.SetStateAction<string>>;
    setSelectedLocationId: React.Dispatch<React.SetStateAction<string>>;
    setOpenPlaceOfServiceModal: Function;
    setTeamName: React.Dispatch<React.SetStateAction<string>>;
    setOrgId: React.Dispatch<React.SetStateAction<string>>;
    setTeamId: React.Dispatch<React.SetStateAction<string>>;
};

const OrganizationCollapseView: React.FC<Props> = ({
    data,
    openModal,
    setSelectedLocation,
    setOpenLocationModal,
    setSelectedOrganization,
    setSelectedLocationId,
    setTeamName,
    setOpenPlaceOfServiceModal,
    setOrgId,
    setTeamId,
}) => {
    const navigate = useNavigate();
    
    const addressFieldText = (address: AddressResponse) => {
        if (address === undefined) return 'N/A';
        
        let addressText = [
            address?.streetAddress,
            address?.streetAddress2,
            address?.city,
            address?.state,
            address?.zipCode,
            address?.country
        ].filter(part => !Utility.isNullOrUndefinedOrEmpty(part)).join(', ');
        
        return Utility.isNullOrUndefinedOrEmpty(addressText) ? "N/A" : addressText;
    }

    return (
        <Collapse className="bg-transparent organization-collapse">
            {data.map((organization) => (
                <Panel
                    showArrow={true}
                    className="mb-2 bg-collapse-light-blue"
                    key={organization.id}
                    header={
                        <div className="d-flex align-items-center justify-content-between">
                            <OrganizationHeader
                                headerText={`Organization: ${organization.hospiceName}`}
                            />
                            <div>
                                <RoundedButtonWithBorder
                                    className="br-medium-blue text-medium-blue bg-white me-2"
                                    border="5px"
                                    onClick={() =>
                                        navigate(
                                            `${organization.id}/order-sets?orgName=${organization.hospiceName}`,
                                        )
                                    }
                                >
                                    Comfort Kits/Order Sets
                                </RoundedButtonWithBorder>
                                <RoundedButton
                                    className="bg-medium-blue text-white"
                                    onClick={() =>
                                        navigate(`${organization.id}/users`, {
                                            state: organization,
                                        })
                                    }
                                >
                                    Manage Users
                                </RoundedButton>
                            </div>
                        </div>
                    }
                >
                    <div>
                        <div className="d-flex justify-content-end">
                            <PlusButton
                                className="mb-2"
                                onClick={() => {
                                    setSelectedOrganization(
                                        organization.hospiceName,
                                    );
                                    setOrgId(organization.id);
                                    setOpenLocationModal(true);
                                }}
                            >
                                New Location
                            </PlusButton>
                        </div>

                        <Row>
                            <Col span={4} className="p-3 fs-7 bg-grey border border-dark-subtle">
                                Address
                            </Col>
                            <Col span={20} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                {addressFieldText(organization.address)}
                            </Col>
                        </Row>

                        <Row>
                            <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                Phone
                            </Col>
                            <Col span={4} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                {organization.phoneNumber ?? "N/A"}
                            </Col>
                            <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                Email
                            </Col>
                            <Col span={4} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                {organization.adminEmailAddress ?? "N/A"}
                            </Col>
                            <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                Organization Code
                            </Col>
                            <Col span={4} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                {organization.hospiceRefId}
                            </Col>
                        </Row>

                        <Row>
                            <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                Effective Date
                            </Col>
                            <Col span={8} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                {Utility.getFormattedDate(organization.effectiveDate)}
                            </Col>

                            <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                Implementation Date
                            </Col>
                            <Col span={8} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                {Utility.getFormattedDate(organization.implementationDate)}
                            </Col>
                        </Row>
                    </div>

                    {organization.hospiceLocations.map((location) => (
                        <Collapse
                            key={location.id}
                            bordered={false}
                            className="custom-collapse"
                        >
                            <Collapse.Panel
                                header={
                                    <OrganizationHeader
                                        headerText={`Location: ${location.description}`}
                                    />
                                }
                                key={location.id}
                                className="mb-2 bg-collapse-light-blue"
                            >
                                <div className="d-flex justify-content-end">
                                    <PlusButton
                                        className="mb-2"
                                        onClick={() => {
                                            setSelectedLocation(
                                                location.description,
                                            );
                                            setSelectedLocationId(location.id);
                                            openModal(true);
                                        }}
                                    >
                                        New Team
                                    </PlusButton>
                                </div>

                                <Row className="mb-2">
                                    <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                        {' '}
                                        Location ID{' '}
                                    </Col>
                                    <Col span={8} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                        {location.rxLogicId}
                                    </Col>

                                    <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                        Location Code
                                    </Col>
                                    <Col span={8} className="bg-white p-3 fs-7 bg-grey border border-dark-subtle">
                                        {location.code}
                                    </Col>
                                </Row>

                                {location.hospiceTeams.map((team) => (
                                    <Collapse
                                        bordered={false}
                                        key={team.id}
                                        className="custom-collapse"
                                    >
                                        <Collapse.Panel
                                            header={
                                                <OrganizationHeader
                                                    headerText={`Team: ${team.teamName}`}
                                                />
                                            }
                                            key={team.id}
                                            className="mb-2 bg-collapse-light-blue"
                                        >
                                            <div className="d-flex justify-content-end">
                                                <PlusButton
                                                    className="mb-2"
                                                    onClick={() => {
                                                        setTeamName(
                                                            team.teamName,
                                                        );
                                                        setTeamId(team.id);
                                                        setOpenPlaceOfServiceModal(
                                                            true,
                                                        );
                                                    }}
                                                >
                                                    New Place of Service
                                                </PlusButton>
                                            </div>
                                            
                                            <Row>
                                                <Col span={4} className="p-3 fs-7 bg-grey border border-dark-subtle">
                                                    Address
                                                </Col>
                                                <Col span={20} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                                    {addressFieldText(team.address)}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                                    Phone
                                                </Col>
                                                <Col span={8} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                                    {team.phone ?? "N/A"}
                                                </Col>
                                                <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                                    Email
                                                </Col>
                                                <Col span={8} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                                    {team.email ?? "N/A"}
                                                </Col>
                                            </Row>

                                            <Row className="mb-2">
                                                <Col
                                                    xs={24}
                                                    md={4}
                                                    className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle"
                                                >
                                                    RxLogic's Team ID
                                                </Col>
                                                <Col
                                                    xs={24}
                                                    md={4}
                                                    className="p-3 fs-7 bg-white bg-grey border border-dark-subtle"
                                                >
                                                    {team.rxLogicId}
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={4}
                                                    className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle"
                                                >
                                                    ScriptSure's Team ID
                                                </Col>
                                                <Col
                                                    xs={24}
                                                    md={4}
                                                    className="p-3 fs-7 bg-white bg-grey border border-dark-subtle"
                                                >
                                                    {team.scriptSureId}
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={4}
                                                    className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle"
                                                >
                                                    RxLogic's Team Code
                                                </Col>
                                                <Col
                                                    xs={24}
                                                    md={4}
                                                    className="p-3 fs-7 bg-white bg-grey border border-dark-subtle"
                                                >
                                                    {team.code}
                                                </Col>
                                            </Row>

                                            {team.placeOfServices.map(
                                                (service) => (
                                                    <Collapse
                                                        bordered={false}
                                                        key={service.id}
                                                        className="custom-collapse"
                                                    >
                                                        <Collapse.Panel
                                                            header={
                                                                <OrganizationHeader
                                                                    headerText={`Place of Service: ${service.name.toUpperCase()}`}
                                                                />
                                                            }
                                                            key={service.id}
                                                            className="mb-2 bg-collapse-light-blue"
                                                        >
                                                            <Row className="mb-2">
                                                                <Col
                                                                    span={5}
                                                                    className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle"
                                                                >
                                                                    Place of
                                                                    Service ID
                                                                </Col>
                                                                <Col
                                                                    span={7}
                                                                    className="p-3 fs-7 bg-white bg-grey border border-dark-subtle"
                                                                >
                                                                    {
                                                                        service.rxLogicId
                                                                    }
                                                                </Col>

                                                                <Col
                                                                    span={5}
                                                                    className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle"
                                                                >
                                                                    Place of
                                                                    Service Code
                                                                </Col>
                                                                <Col
                                                                    span={7}
                                                                    className="p-3 fs-7 bg-white bg-grey border border-dark-subtle"
                                                                >
                                                                    {
                                                                        service.code
                                                                    }
                                                                </Col>
                                                            </Row>
                                                        </Collapse.Panel>
                                                    </Collapse>
                                                ),
                                            )}
                                        </Collapse.Panel>
                                    </Collapse>
                                ))}
                            </Collapse.Panel>
                        </Collapse>
                    ))}
                </Panel>
            ))}
        </Collapse>
    );
};

export default OrganizationCollapseView;
